import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Music, Play } from 'lucide-react'

import type { ProductionAsset } from '@/api/studio-production'
import { resolveMediaDisplayUrl } from '@/features/studio/lib/media-display'
import { ImagePreviewModal } from '@/features/studio/modals/ImagePreviewModal'
import { MediaPreviewModal } from '@/features/studio/modals/MediaPreviewModal'
import { AssetAudioWavePlayer } from '@/features/studio/pages/assets/AssetAudioWavePlayer'
import { studioCatalogCardShellClass } from '@/features/studio/components/StudioCatalogCard'
import { CatalogListCardContent } from '@/shared/components/catalog/CatalogListCardContent'
import { CATALOG_LIST_COVER_FALLBACK_ASPECT } from '@/shared/components/catalog/catalogListGrid'
import { ProtoBadge, ProtoThumb } from '@/features/studio/components/ui-kit'
import { Button } from '@/shared/components/ui/button'
import { ASSET_KIND_LABEL_KEYS, assetMediaCategory, assetMediaUrl, isRegisteredAsset, visibleAssetTags } from '@/features/studio/pages/assets/asset-utils'

function AssetThumbnail({ asset }: { asset: ProductionAsset }) {
  const category = assetMediaCategory(asset)
  const rawUrl = assetMediaUrl(asset)
  const displayUrl = rawUrl ? resolveMediaDisplayUrl(rawUrl) : ''
  const [failed, setFailed] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)

  useEffect(() => {
    setFailed(false)
  }, [displayUrl])

  if (category === 'image' && displayUrl && !failed) {
    return (
      <>
        <Button
          type="button"
          variant="ghost"
          onClick={() => setPreviewOpen(true)}
          className="absolute inset-0 block h-full w-full overflow-hidden rounded-none p-0 bg-[#0e0e16] hover:bg-[#0e0e16]"
        >
          <img
            src={displayUrl}
            alt=""
            className="h-full w-full object-contain transition hover:scale-[1.02]"
            onError={() => setFailed(true)}
          />
        </Button>
        <ImagePreviewModal
          open={previewOpen}
          urls={rawUrl ? [rawUrl] : []}
          index={0}
          title={asset.name}
          onClose={() => setPreviewOpen(false)}
          onIndexChange={() => {}}
        />
      </>
    )
  }

  if (category === 'video' && displayUrl) {
    return (
      <>
        <Button
          type="button"
          variant="ghost"
          onClick={() => setPreviewOpen(true)}
          className="absolute inset-0 block h-full w-full overflow-hidden rounded-none p-0 bg-[#0e0e16] hover:bg-[#0e0e16]"
          aria-label={asset.name}
        >
          <video src={displayUrl} className="h-full w-full object-contain" muted preload="metadata" playsInline />
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/30 transition group-hover:bg-black/40">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-black/55 ring-1 ring-white/20">
              <Play className="ml-0.5 h-5 w-5 text-white" fill="currentColor" />
            </span>
          </span>
        </Button>
        <MediaPreviewModal
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          url={rawUrl ?? displayUrl}
          kind="video"
          title={asset.name}
        />
      </>
    )
  }

  if (category === 'audio') {
    if (displayUrl) {
      return (
        <>
          <AssetAudioWavePlayer
            src={displayUrl}
            title={asset.name}
            onError={() => setFailed(true)}
            onExpand={() => setPreviewOpen(true)}
          />
          <MediaPreviewModal
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            url={rawUrl ?? displayUrl}
            kind="audio"
            title={asset.name}
          />
        </>
      )
    }
    return (
      <div className="flex h-full w-full items-center justify-center bg-[#0e0e16]">
        <Music className="h-10 w-10 text-zinc-600" />
      </div>
    )
  }

  return (
    <div className="h-full w-full overflow-hidden bg-[#0e0e16]">
      <ProtoThumb seed={asset.id} aspect="square" className="h-full w-full" flush />
    </div>
  )
}

export function AssetCard({
  asset,
  actions,
}: {
  asset: ProductionAsset
  actions: ReactNode
}) {
  const { t } = useTranslation()

  return (
    <article className={studioCatalogCardShellClass('overflow-hidden text-left')}>
      <div className={`relative w-full ${CATALOG_LIST_COVER_FALLBACK_ASPECT} shrink-0 overflow-hidden`}>
        <AssetThumbnail asset={asset} />
      </div>
      <CatalogListCardContent
        className="!aspect-auto min-h-[72px]"
        title={asset.name}
        summary={asset.note}
        hideFooter
        footerBlock={
          <div className="space-y-1.5">
            <div className="flex flex-wrap gap-1">
              {isRegisteredAsset(asset) ? (
                <ProtoBadge tone="default">{asset.status}</ProtoBadge>
              ) : (
                <ProtoBadge tone="amber">{t('assets.unregistered')}</ProtoBadge>
              )}
              <ProtoBadge tone="indigo">
                {t(ASSET_KIND_LABEL_KEYS[asset.kind] ?? 'assets.kind.misc', { defaultValue: asset.kind })}
              </ProtoBadge>
              {visibleAssetTags(asset.tags).slice(0, 2).map((tag) => (
                <ProtoBadge key={tag} tone="default">
                  {tag}
                </ProtoBadge>
              ))}
            </div>
            {actions}
          </div>
        }
      />
    </article>
  )
}
